import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Drawer, Input, Skeleton, Space, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { listComparableRuns, type ComparableRunsView } from '@/api/validationRuns';
import { StatusPill } from '@/components/StatusPill';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { identityLabel } from './compareWords';
import { useRunLinks } from '@/features/scenario-intelligence/workspace/paths';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// Choosing the candidate run (SI-07, mockup 09 frame D). A picker is a drawer. Each run lists its identity and says,
// BEFORE it is chosen, which comparison type it would make with the baseline. A run is held back only when it lacks
// evidence the comparison needs — never for its age — and stays listed and inspectable. Runs of other practices are
// not listed: a comparison stays inside one practice. The server decides all of it (Compare/Runs).
// ─────────────────────────────────────────────────────────────────────────────

export const ChooseRunsDrawer = observer(function ChooseRunsDrawer({
  open,
  baselineRunId,
  currentCandidateId,
  onClose,
  onChoose,
}: {
  open: boolean;
  baselineRunId: number;
  currentCandidateId?: number | null;
  onClose: () => void;
  onChoose: (candidateRunId: number) => void;
}) {
  const { themeStore, merchantContextStore } = useStore();
  const links = useRunLinks();
  const isDark = themeStore.mode === 'dark';
  const practice = merchantContextStore.selectedMerchantId;
  const [view, setView] = useState<ComparableRunsView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<number | null>(currentCandidateId ?? null);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setView(null);
    setError(null);
    setPicked(currentCandidateId ?? null);
    listComparableRuns(baselineRunId, null, controller.signal)
      .then((v) => !controller.signal.aborted && setView(v))
      .catch((err: Error) => !controller.signal.aborted && setError(err.message));
    return () => controller.abort();
    // A practice switch is a new context: the list is read again for the new practice (MOD-G1).
  }, [open, baselineRunId, currentCandidateId, practice]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (view?.runs ?? []).filter((r) => !term
      || `#${r.identity.runId} ${identityLabel(r.identity)} ${r.summary}`.toLowerCase().includes(term));
  }, [view, search]);
  const border = isDark ? colors.slate[700] : colors.slate[200];
  const selectedBg = isDark ? `${colors.ink[700]}40` : colors.ink[50];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="min(640px, 100vw)"
      title="Choose the candidate run"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" disabled={picked == null} onClick={() => picked != null && onChoose(picked)}>
            {picked != null ? `Compare with #${picked}` : 'Compare'}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {view && <Text type="secondary">{`Baseline: run #${baselineRunId} · ${identityLabel(view.baseline)}`}</Text>}
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search runs by number, revision or deployment…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search runs"
        />
        {error && <Alert type="error" showIcon title="Could not list the runs" description={`The server said: "${error}"`} />}
        {!view && !error && <Skeleton active paragraph={{ rows: 5 }} />}
        {view && rows.length === 0 && (
          <Text type="secondary">{view.runs.length === 0 ? 'No other run of this agent in this practice yet.' : 'No run matches the search.'}</Text>
        )}
        <div role="radiogroup" aria-label="Candidate runs" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((r) => {
            const id = r.identity;
            const selected = picked === id.runId;
            return (
              <div
                key={id.runId}
                role="radio"
                aria-checked={selected}
                aria-disabled={r.heldBack}
                tabIndex={r.heldBack ? -1 : 0}
                onClick={() => !r.heldBack && setPicked(id.runId)}
                onKeyDown={(e) => { if (!r.heldBack && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setPicked(id.runId); } }}
                style={{
                  border: `1px solid ${selected ? colors.ink[500] : border}`,
                  background: selected ? selectedBg : undefined,
                  borderRadius: 8,
                  padding: '10px 12px',
                  cursor: r.heldBack ? 'default' : 'pointer',
                  opacity: r.heldBack ? 0.85 : 1,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <Text strong style={{ overflowWrap: 'anywhere' }}>
                    {`#${id.runId} · ${dayjs(id.runDate).format('D MMM HH:mm')} · ${identityLabel(id)}`}
                  </Text>
                  <StatusPill tone={r.heldBack ? 'slate' : r.verdict === 'Confounded' ? 'amber' : 'green'}>{r.summary}</StatusPill>
                </div>
                <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                  {`${id.trials} trial${id.trials === 1 ? '' : 's'} · ${id.evaluator}`}
                </Text>
                {r.heldBack && (
                  <Space direction="vertical" size={2} style={{ marginTop: 4 }}>
                    <Text style={{ fontSize: 12 }}>{r.heldBackReason}</Text>
                    <Link to={links.run(id.runId)}>{`Open run #${id.runId}`}</Link>
                  </Space>
                )}
              </div>
            );
          })}
        </div>
        {view?.note && <Text type="secondary" style={{ fontSize: 12 }}>{view.note}</Text>}
      </div>
    </Drawer>
  );
});
